import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { userService } from '../../_services';

function OverridePopup({
    text,
    closeEditGroupPopup,
    openManageGroup,
    openResetGroup,
    SelectedGroupNumber,
    MaxComm,
    cPagin,
    cSearch,
    reopenFlag,
    allData,
    userType,
    chkUser,
    confirmationText,
    dIndex,
    userLists
}) {
    const queryClient = useQueryClient();
    const [loading, setLoading] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [userDataList, setUserDataList] = useState([]);
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [searchSubDisplay, setSearchSubDisplay] = useState({});  // Changed to object indexed by row index
    const [openSubPopover, setOpenSubPopover] = useState({});
    const [openDistributorPopover, setOpenDistributorPopover] = useState(false);
    const [isUserAvail, setIsUserAvail] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [advanceSetting, setAdvanceSetting] = useState(false);
    const [whAdvanceSetting, setWhAdvanceSetting] = useState({});
    const [isBroker, setIsBroker] = useState(false);
    const [originalSubdistributorData, setOriginalSubdistributorData] = useState({});

    // Field errors
    const [disNameError, setDisNameError] = useState('');
    const [disEmailError, setDisEmailError] = useState('');
    const [disPhoneError, setDisPhoneError] = useState('');
    const [disCommissionError, setDisCommissionError] = useState('');

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchAdvanceSetting = useCallback(async () => {
        if (!userdata) return;

        try {
            const apidata = await userService.GetAdvanceSetting('', '', userdata.wholesaler_Id.toString());
            if (apidata?.[0]?.settings?.[0]) {
                setWhAdvanceSetting(apidata[0].settings[0]);
            }
        } catch (err) {
            // Error fetching advance settings
        }
    }, [userdata]);

    const fetchUserList = useCallback(async () => {
        if (!userdata) return;

        try {
            const response = await fetch('/api/ws/groupnamedetails', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    WhName: userdata.paramsname,
                    GroupNumber: SelectedGroupNumber
                })
            });

            const text = await response.text();
            const apidata = text ? JSON.parse(text) : [];

            fetchAdvanceSetting();

            if (apidata?.[0]?.distributor_UserId !== "0") {
                setIsUserAvail(true);
                setIsEdit(true);
            }

            // Process data
            const processedData = apidata.map(a => ({
                ...a,
                distributorCommission: a.distributorCommission === "0" ? "1.75" : a.distributorCommission,
                userType: userType || a.userType,
                subDistributor: a.subDistributor?.map(sub => ({
                    ...sub,
                    subDistributorCommission: sub.subDistributorCommission === "0" ? "0.50" : sub.subDistributorCommission,
                    isineditedmode: false,
                    newRow: false
                })) || []
            }));

            setUserDataList(processedData);

            // Handle confirmation text - moved to useEffect below
        } catch (err) {
            // Error fetching user list
        }
    }, [userdata, SelectedGroupNumber, userType, fetchAdvanceSetting]);

    useEffect(() => {
        // If we have a confirmation response, handle it first using userLists prop
        if (confirmationText === 'yes') {
            if (userLists?.[0]?.subDistributor) {
                const subdislist = [...userLists[0].subDistributor];
                subdislist.splice(dIndex, 1);
                setUserDataList([{ ...userLists[0], subDistributor: subdislist }]);
                setLoading(false);
            }
        } else if (confirmationText === 'no') {
            if (userLists) {
                setUserDataList(userLists);
                setLoading(false);
            }
        } else {
            // No confirmation, fetch fresh data
            fetchUserList();
        }
        
        const timer = setTimeout(() => {
            if (userdata?.paramsname?.toLowerCase() === 'patient rx discount') {
                setSubmitDisabled(true);
            } else {
                setSubmitDisabled(false);
            }
        }, 700);
        
        return () => clearTimeout(timer);
    }, [confirmationText, userLists, dIndex, fetchUserList, userdata]);

    const handleFieldChange = useCallback((field, value) => {
        setError('');
        
        setUserDataList(prev => {
            if (!prev[0]) return prev;
            return [{ ...prev[0], [field]: value }];
        });

        // Clear specific errors
        if (field === 'distributorName') setDisNameError('');
        if (field === 'emailId') setDisEmailError('');
        if (field === 'phoneNumber') setDisPhoneError('');
        if (field === 'distributorCommission') setDisCommissionError('');
    }, []);

    const searchDistributor = useCallback(async (searchText) => {
        if (isUserAvail || !userdata || !searchText) {
            setSearchDisplay([]);
            return;
        }

        try {
            const response = await fetch('/api/ws/Search_Distributor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    UserName: searchText,
                    WhName: userdata.wholesaler_Id.toString(),
                    SearchType: 'Name'
                })
            });
            const text = await response.text();
            const apidata = text ? JSON.parse(text) : [];
            setSearchDisplay(apidata || []);
            if (apidata && apidata.length > 0) setOpenDistributorPopover(true);
            if (apidata?.[0]?.isBroker !== undefined) {
                setIsBroker(apidata[0].isBroker);
            }
        } catch (err) {
            // Search error
        }
    }, [isUserAvail, userdata]);

    const searchSubDistributor = useCallback(async (searchText, index) => {
        if (!userdata || !searchText) {
            setSearchSubDisplay(prev => ({ ...prev, [index]: [] }));
            return;
        }

        try {
            const response = await fetch('/api/ws/Search_SubDistributor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    GroupNumber: SelectedGroupNumber,
                    UserName: searchText,
                    WhName: userdata.wholesaler_Id.toString(),
                    SearchType: 'Name'
                })
            });
            const text = await response.text();
            const data = text ? JSON.parse(text) : [];
            setSearchSubDisplay(prev => ({ ...prev, [index]: data || [] }));
        } catch (err) {
            // Sub search error
        }
    }, [userdata, SelectedGroupNumber]);

    const handleSubDistributorSelect = useCallback((user, index) => {
        if (!user) {
            return;
        }
        
        setUserDataList(prev => {
            if (!prev[0]?.subDistributor) {
                return prev;
            }
            
            const updatedSubs = [...prev[0].subDistributor];
            const currentSub = updatedSubs[index];
            
            // Preserve commission if user has edited it
            // Only use database commission if current value is empty, "0", or default "0.50"
            const shouldPreserveCommission = currentSub.subDistributorCommission && 
                                             currentSub.subDistributorCommission !== '' && 
                                             currentSub.subDistributorCommission !== '0' &&
                                             currentSub.subDistributorCommission !== '0.00' &&
                                             currentSub.subDistributorCommission !== '0.50';
            
            updatedSubs[index] = {
                ...updatedSubs[index],
                subDistributorName: user.userName,
                subDistributorEmail: user.userEmail,
                subDistributorPhone: user.userPhone,
                // Preserve edited commission or use database value with fallback
                subDistributorCommission: shouldPreserveCommission 
                    ? currentSub.subDistributorCommission 
                    : (user.userComm || currentSub.subDistributorCommission || '0.50')
            };
            
            return [{ ...prev[0], subDistributor: updatedSubs }];
        });
        
        // Clear search results for this row
        setSearchSubDisplay(prev => ({ ...prev, [index]: [] }));
        setOpenSubPopover(prev => ({ ...prev, [index]: false }));
    }, []);

    const handleDistributorSelect = useCallback(async (item) => {
        if (!userdata) return;

        try {
            const response = await fetch('/api/ws/Search_Distributor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    UserName: item.userName,
                    WhName: userdata.wholesaler_Id.toString(),
                    SearchType: 'Details'
                })
            });
            const text = await response.text();
            const apidata = text ? JSON.parse(text) : [];

            if (apidata?.[0]) {
                setUserDataList(prev => {
                    if (!prev[0]) return prev;
                    return [{
                        ...prev[0],
                        distributorName: apidata[0].userName,
                        emailId: isUserAvail ? prev[0].emailId : apidata[0].userEmail,
                        phoneNumber: apidata[0].userPhone
                    }];
                });
                setIsBroker(apidata[0].isBroker);
            }
            setSearchDisplay([]);
            setOpenDistributorPopover(false);
            setDisNameError('');
            setDisEmailError('');
        } catch (err) {
            setError('Oops! Something went wrong. Try Again.');
        }
    }, [userdata, isUserAvail]);

    const addNewSubdistributor = useCallback(() => {
        setUserDataList(prev => {
            if (!prev[0]) return prev;
            
            const newSub = {
                subDisId: Math.floor(10000 + Math.random() * 90000),
            subDistributorName: '',
                subDistributorEmail: '',
            subDistributorPhone: '',
                subDistributorCommission: '0.50',
            isineditedmode: true,
            newRow: true
            };
            
            return [{
                ...prev[0],
                subDistributor: [...(prev[0].subDistributor || []), newSub]
            }];
        });
    }, []);

    const toggleEditSubdistributor = useCallback((index) => {
        setUserDataList(prev => {
            if (!prev[0]?.subDistributor) return prev;
            
            const sub = prev[0].subDistributor[index];
            
            // Store original data when entering edit mode
            if (!sub.isineditedmode) {
                setOriginalSubdistributorData(prevOriginal => ({
                    ...prevOriginal,
                    [index]: { ...sub }
                }));
            }
            
            const updatedSubs = prev[0].subDistributor.map((s, i) => ({
                ...s,
                isineditedmode: i === index ? !s.isineditedmode : s.isineditedmode
            }));
            
            return [{ ...prev[0], subDistributor: updatedSubs }];
        });
    }, []);

    const updateSubdistributor = useCallback((index, field, value) => {
        setUserDataList(prev => {
            if (!prev[0]?.subDistributor) return prev;
            
            const updatedSubs = [...prev[0].subDistributor];
            updatedSubs[index] = { ...updatedSubs[index], [field]: value };
            
            return [{ ...prev[0], subDistributor: updatedSubs }];
        });
        
        // Trigger search when name field changes
        if (field === 'subDistributorName') {
            if (value && value.length > 0) {
                setOpenSubPopover(prev => ({ ...prev, [index]: true }));
                searchSubDistributor(value, index);
            } else {
                // Clear dropdown when field is empty
                setSearchSubDisplay(prev => ({ ...prev, [index]: [] }));
                setOpenSubPopover(prev => ({ ...prev, [index]: false }));
            }
        }
    }, [searchSubDistributor]);

    const saveSubdistributor = useCallback((index) => {
        const sub = userDataList[0]?.subDistributor?.[index];
        if (!sub) return;

        // Validate
        if (!sub.subDistributorName || !sub.subDistributorEmail || !sub.subDistributorCommission) {
            setError('Please check your overrides. All fields are required.');
            return;
        }

        if (!userService.ValidateEmail(sub.subDistributorEmail)) {
            setError('Invalid email for override');
            return;
        }

        if (!userService.validateCurrency(sub.subDistributorCommission)) {
            setError('Invalid commission for override');
            return;
        }

        setUserDataList(prev => {
            if (!prev[0]?.subDistributor) return prev;
            
            const updatedSubs = [...prev[0].subDistributor];
            updatedSubs[index] = { ...updatedSubs[index], isineditedmode: false, newRow: false };
            
            return [{ ...prev[0], subDistributor: updatedSubs }];
        });
        
        // Clear stored original data after successful save
        setOriginalSubdistributorData(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
        
        setError('');
    }, [userDataList]);

    const cancelSubdistributor = useCallback((index) => {
        const sub = userDataList[0]?.subDistributor?.[index];
        if (!sub) return;

        if (sub.newRow) {
            // Remove if new row
            setUserDataList(prev => {
                if (!prev[0]?.subDistributor) return prev;
                const updatedSubs = prev[0].subDistributor.filter((_, i) => i !== index);
                return [{ ...prev[0], subDistributor: updatedSubs }];
            });
        } else {
            // Restore original data and exit edit mode
            setUserDataList(prev => {
                if (!prev[0]?.subDistributor) return prev;
                
                const updatedSubs = [...prev[0].subDistributor];
                
                // Restore from original data if available
                if (originalSubdistributorData[index]) {
                    updatedSubs[index] = {
                        ...originalSubdistributorData[index],
                        isineditedmode: false
                    };
                } else {
                    updatedSubs[index] = {
                        ...updatedSubs[index],
                        isineditedmode: false
                    };
                }
                
                return [{ ...prev[0], subDistributor: updatedSubs }];
            });
            
            // Clear stored original data
            setOriginalSubdistributorData(prev => {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            });
        }
    }, [userDataList, originalSubdistributorData]);

    const deleteSubdistributor = useCallback((index) => {
        const sub = userDataList[0]?.subDistributor?.[index];
        if (!sub) return;

        // If it's a new unsaved row, delete immediately without confirmation
        if (sub.newRow) {
            setUserDataList(prev => {
                if (!prev[0]?.subDistributor) return prev;
                const updatedSubs = prev[0].subDistributor.filter((_, i) => i !== index);
                return [{ ...prev[0], subDistributor: updatedSubs }];
            });
        } else {
            // For saved rows, show confirmation popup
            closeEditGroupPopup?.(index, 'DeleteReps', '', userDataList, '', SelectedGroupNumber);
            openResetGroup?.("Are you sure want to delete this override?", SelectedGroupNumber);
        }
    }, [userDataList, closeEditGroupPopup, openResetGroup, SelectedGroupNumber]);

    const validateDistributor = useCallback(() => {
        const data = userDataList[0];
        if (!data) return false;

        let isValid = true;
        setDisNameError('');
        setDisEmailError('');
        setDisCommissionError('');
        setDisPhoneError('');
        setError('');
        setLoading(true);
        setSubmitDisabled(true);

        // Name validation
        if (!data.distributorName) {
            setDisNameError('Name required');
            isValid = false;
        } else if (data.distributorName.indexOf('.') !== -1) {
            setDisNameError('Dot not allowed');
            isValid = false;
        } else if (!/^\w+(\s\w+)*$/.test(data.distributorName)) {
            setDisNameError('Space not allowed');
            isValid = false;
        }

        // Email validation
        if (!data.emailId) {
            setDisEmailError('Email required');
            isValid = false;
        } else if (data.emailId.indexOf(' ') >= 0) {
            setDisEmailError('Space not allowed');
            isValid = false;
        } else if (!userService.ValidateEmail(data.emailId)) {
            setDisEmailError('Invalid email');
            isValid = false;
        }

        // Commission validation
        if (!data.distributorCommission) {
            setDisCommissionError('Commission required');
            isValid = false;
        } else if (data.distributorCommission.indexOf(' ') >= 0) {
            setDisCommissionError('Space not allowed');
            isValid = false;
        } else if (!userService.validateCurrency(data.distributorCommission)) {
            setDisCommissionError('Invalid Commission');
            isValid = false;
        }

        if (!isValid) {
            setLoading(false);
            setSubmitDisabled(false);
        }

        return isValid;
    }, [userDataList]);

    const checkCommissionExceed = useCallback(() => {
        if (!userDataList[0]) return false;
        
        let totalComm = parseFloat(userDataList[0].distributorCommission || 0);
        userDataList[0].subDistributor?.forEach(sub => {
            totalComm += parseFloat(sub.subDistributorCommission || 0);
        });
        
        return totalComm <= parseFloat(MaxComm) || userDataList[0].IsRuleType;
    }, [userDataList, MaxComm]);

    const isSubdistributorInEditMode = useCallback(() => {
        return userDataList[0]?.subDistributor?.some(sub => sub.isineditedmode) || false;
    }, [userDataList]);

    const validateUserData = useCallback(() => {
        const data = userDataList[0];
        if (!data?.subDistributor || data.subDistributor.length <= 1) return true;

        const subs = data.subDistributor;
        const unique = subs.filter((sub, index, self) =>
            index === self.findIndex(t =>
                t.subDistributorEmail === sub.subDistributorEmail ||
                t.subDistributorName === sub.subDistributorName
            )
        );

        return subs.length === unique.length;
    }, [userDataList]);

    const handleSaveSubmit = useCallback(async () => {
        if (!validateDistributor()) return;

        if (isSubdistributorInEditMode()) {
            setError('Please save each override before saving changes');
            setLoading(false);
            setSubmitDisabled(false);
            return;
        }

        if (!checkCommissionExceed()) {
            setError('The total commission for this group number exceeds the maximum allowed commission');
            setLoading(false);
            setSubmitDisabled(false);
            return;
        }

        if (!validateUserData()) {
            setError('Duplicate overrides not allowed');
            setLoading(false);
            setSubmitDisabled(false);
            return;
        }

        try {
            const apidata = await userService.ProcessUser(
                JSON.stringify(userDataList),
                userdata.wholesaler_Id.toString(),
                SelectedGroupNumber,
                MaxComm
            );

            if (apidata?.[0]?.code === "1") {
                setLoading(false);
                setSuccessMessage(apidata[0].message);
                setError('');
                setSubmitDisabled(true);
                
                // Invalidate cache so ManageGroupNumber refetches fresh data
                queryClient.invalidateQueries({
                    queryKey: ['manageGroupUsers'],
                    exact: false
                });
                
                if (chkUser === "true" && userDataList[0]?.userType === "Broker") {
                    setAdvanceSetting(true);
                }
            } else {
                setLoading(false);
                setError(apidata?.[0]?.message || 'Whoops, something went wrong. Please try again');
                setSuccessMessage('');
                setSubmitDisabled(false);
            }
        } catch (err) {
            setLoading(false);
            setError('Whoops, something went wrong. Please try again');
            setSubmitDisabled(false);
        }
    }, [validateDistributor, isSubdistributorInEditMode, checkCommissionExceed, validateUserData, userDataList, userdata, SelectedGroupNumber, MaxComm, chkUser, queryClient]);

    const handleBack = useCallback(() => {
        closeEditGroupPopup?.();
        openManageGroup?.(SelectedGroupNumber, MaxComm, cPagin, cSearch, '', '', reopenFlag, allData);
    }, [closeEditGroupPopup, openManageGroup, SelectedGroupNumber, MaxComm, cPagin, cSearch, reopenFlag, allData]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            handleBack();
        }
    }, [handleBack]);

    const userData = userDataList[0];
    const isDisabled = userdata?.paramsname?.toLowerCase() === 'patient rx discount';

                                                                            return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4 min-h-[400px]">
                    {userData && (
                        <div className="space-y-6">
                            {/* Distributor Fields */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label>Name</Label>
                                    <Popover
                                        open={openDistributorPopover}
                                        onOpenChange={(open) => setOpenDistributorPopover(open)}
                                    >
                                        <PopoverTrigger asChild>
                                            <div className="relative">
                                                <Input
                                                    type="text"
                                                    value={userData.distributorName || ''}
                                                    onChange={(e) => {
                                                        handleFieldChange('distributorName', e.target.value);
                                                        searchDistributor(e.target.value);
                                                    }}
                                                    onFocus={() => {
                                                        if (searchDisplay.length > 0) setOpenDistributorPopover(true);
                                                    }}
                                                    placeholder="Search user..."
                                                />
                                                {userData.distributorName && !isUserAvail && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                                                        onClick={() => {
                                                            handleFieldChange('distributorName', '');
                                                            setSearchDisplay([]);
                                                            setOpenDistributorPopover(false);
                                                        }}
                                                    >
                                                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                                    </Button>
                                                )}
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-[300px] p-0"
                                            align="start"
                                            onOpenAutoFocus={(e) => e.preventDefault()}
                                        >
                                            <div className="max-h-[110px] overflow-y-auto">
                                                {searchDisplay.length > 0 ? (
                                                    searchDisplay.map((item, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="px-3 py-2 cursor-pointer hover:bg-accent text-sm border-b last:border-b-0"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                handleDistributorSelect(item);
                                                            }}
                                                        >
                                                            {item.userName}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-sm text-muted-foreground">No user found.</div>
                                                )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                    {disNameError && (
                                        <p className="text-sm text-destructive mt-1">{disNameError}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={userData.emailId || ''}
                                        onChange={(e) => handleFieldChange('emailId', e.target.value)}
                                        readOnly={isUserAvail}
                                    />
                                    {disEmailError && (
                                        <p className="text-sm text-destructive mt-1">{disEmailError}</p>
                                    )}
                                        </div>

                                <div className="flex flex-col gap-2">
                                    <Label>Phone Number</Label>
                                    <Input
                                        type="text"
                                        value={userData.phoneNumber || ''}
                                        onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                                    />
                                    {disPhoneError && (
                                        <p className="text-sm text-destructive mt-1">{disPhoneError}</p>
                                    )}
                                        </div>
                                        </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label>Commission ($)</Label>
                                    <Input
                                        type="text"
                                        value={userData.distributorCommission || ''}
                                        onChange={(e) => handleFieldChange('distributorCommission', e.target.value)}
                                    />
                                    {disCommissionError && (
                                        <p className="text-sm text-destructive mt-1">{disCommissionError}</p>
                                    )}
                                                                                                        </div>
                                                                                                </div>

                            {/* Overrides Section */}
                            {userData.userType !== "IBO/Marketplace" && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold">Overrides</h3>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={addNewSubdistributor}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add New Override
                                        </Button>
                                    </div>

                                    {userData.subDistributor?.length > 0 ? (
                                        <div className="rounded-md border">
                                            <Table wrapperClassName="relative w-full overflow-visible">
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>Email</TableHead>
                                                        <TableHead>Phone</TableHead>
                                                        <TableHead>Commission ($)</TableHead>
                                                        <TableHead>Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {userData.subDistributor.map((sub, index) => (
                                                        <TableRow key={sub.subDisId}>
                                                            {sub.isineditedmode ? (
                                                                <>
                                                                    <TableCell>
                                                                        <Popover
                                                                            open={openSubPopover[index] || false}
                                                                            onOpenChange={(open) => setOpenSubPopover(prev => ({ ...prev, [index]: open }))}
                                                                        >
                                                                            <PopoverTrigger asChild>
                                                                                <div className="relative">
                                                                                    <Input
                                                                                        size="sm"
                                                                                        value={sub.subDistributorName}
                                                                                        onChange={(e) => {
                                                                                            updateSubdistributor(index, 'subDistributorName', e.target.value);
                                                                                        }}
                                                                                        onFocus={() => {
                                                                                            setOpenSubPopover(prev => ({ ...prev, [index]: true }));
                                                                                            if (sub.subDistributorName) {
                                                                                                searchSubDistributor(sub.subDistributorName, index);
                                                                                            }
                                                                                        }}
                                                                                        placeholder="Search user..."
                                                                                    />
                                                                                    {sub.subDistributorName && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                                                                                            onClick={() => {
                                                                                                updateSubdistributor(index, 'subDistributorName', '');
                                                                                                setOpenSubPopover(prev => ({ ...prev, [index]: false }));
                                                                                            }}
                                                                                        >
                                                                                            <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent
                                                                                className="w-[300px] p-0"
                                                                                align="start"
                                                                                onOpenAutoFocus={(e) => e.preventDefault()}
                                                                            >
                                                                                <div className="max-h-[110px] overflow-y-auto">
                                                                                    {(searchSubDisplay[index] || []).length > 0 ? (
                                                                                        (searchSubDisplay[index] || []).map((user, idx) => (
                                                                                            <div
                                                                                                key={idx}
                                                                                                className="px-3 py-2 cursor-pointer hover:bg-accent text-sm border-b last:border-b-0"
                                                                                                onMouseDown={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    handleSubDistributorSelect(user, index);
                                                                                                }}
                                                                                            >
                                                                                                {user.userName}
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-muted-foreground">No user found.</div>
                                                                                    )}
                                                                                </div>
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Input
                                                                            size="sm"
                                                                            value={sub.subDistributorEmail}
                                                                            onChange={(e) => updateSubdistributor(index, 'subDistributorEmail', e.target.value)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Input
                                                                            size="sm"
                                                                            value={sub.subDistributorPhone}
                                                                            onChange={(e) => updateSubdistributor(index, 'subDistributorPhone', e.target.value)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Input
                                                                            size="sm"
                                                                            value={sub.subDistributorCommission}
                                                                            onChange={(e) => updateSubdistributor(index, 'subDistributorCommission', e.target.value)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <div className="flex gap-1">
                                                                            <Button
                                                                                onClick={() => saveSubdistributor(index)}
                                                                            >
                                                                                Save
                                                                            </Button>
                                                                            <Button
                                                                                variant="secondary"
                                                                                onClick={() => cancelSubdistributor(index)}
                                                                            >
                                                                                Cancel
                                                                            </Button>
                                            </div>
                                                                    </TableCell>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <TableCell>{sub.subDistributorName}</TableCell>
                                                                    <TableCell>{sub.subDistributorEmail}</TableCell>
                                                                    <TableCell>{sub.subDistributorPhone}</TableCell>
                                                                    <TableCell>{parseFloat(sub.subDistributorCommission).toFixed(2)}</TableCell>
                                                                    <TableCell>
                                                                        <div className="flex gap-1">
                                                                            <Button
                                                                                size="icon"
                                                                                variant="ghost"
                                                                                onClick={() => toggleEditSubdistributor(index)}
                                                                            >
                                                                                <Pencil className="h-4 w-4" />
                                                                            </Button>
                                                                            <Button
                                                                                size="icon"
                                                                                variant="ghost"
                                                                                onClick={() => deleteSubdistributor(index)}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                </div>
                                                                    </TableCell>
                                                                </>
                                                            )}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                            </div>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">No Overrides added yet</p>
                                    )}
                        </div>
                            )}

                            {/* Success Message */}
                            {successMessage && (
                                <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm border border-green-200">
                                    {successMessage}
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <p className="text-sm text-destructive">
                                    {error}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                                <Button 
                                    onClick={handleSaveSubmit}
                                    disabled={submitDisabled || loading || isDisabled}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save & Submit'
                                    )}
                                </Button>
                                <Button variant="secondary" onClick={handleBack}>
                                    Cancel
                                </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default OverridePopup;
